import { useAuth } from "contexts/AuthContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { bugsnagClient } from "../bugsnag";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";
import projectsData from "../data/projectsData.json";
import api from "../utils/api";
import ImageUploadWizard from "./ImageUploadWizard";
import LV from "./LV";
import PdsProjectDetails from "./PdsProjectDetails";
import PdsProjectSelector from "./PdsProjectSelector";
import PlanUploadWizard from "./PlanUploadWizard";
import ProjectEditInline from "./ProjectEditInline";
import SectionManagement from "./SectionManagement";
import TeamManagement from "./TeamManagement";

const ProjectDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showPlanUpload, setShowPlanUpload] = useState(false);

  // Add states for project data loading
  const [projectData, setProjectData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add states for user permissions
  const [userRole, setUserRole] = useState(null);
  const { user } = useAuth();
  const isGlobalAdmin = user?.isGlobalAdmin || false;
  const [sections, setSections] = useState([]);

  // PDS integration state
  const [pdsProjectId, setPdsProjectId] = useState(null);

  // Edit project state
  const [isEditing, setIsEditing] = useState(false);

  // Fetch project data when component mounts or id changes
  useEffect(() => {
    const fetchProjectData = async () => {
      setIsLoading(true);

      try {
        // Validate ID first
        if (!id || id === "undefined" || id === "null") {
          setError(t("invalid_project_id"));
          setProjectData(projectsData.demo);
          setIsLoading(false);
          return;
        }

        // Check if this is a demo project first
        if (projectsData[id]) {
          setProjectData(projectsData[id]);
          setIsLoading(false);
          return;
        }

        // If not a demo project, fetch from API
        const response = await api.fetchProject(id);

        if (response && response.project && !response.message) {
          const data = response.project;

          // Set user permissions from API response
          setUserRole(response.userOrganizationRole);

          // Set PDS project ID if linked
          setPdsProjectId(data.pdsProjectId || null);

          // Format the data to match our expected structure
          const formattedData = {
            title: data.title,
            status: data.status,
            type: data.type,
            location: data.location,
            coordinates: data.coordinates,
            lastUpdated: data.lastUpdated,
            description: data.description,
            metrics: data.metrics || {
              progress: 0,
              area: "N/A",
              height: "N/A",
              estimatedCompletion: "N/A",
            },
            processingDetails: data.processingDetails || {
              videoLength: "N/A",
              framesCaptured: "N/A",
              pointsGenerated: "N/A",
              processingTime: "N/A",
            },
            imageProcessingJobs: data.imageProcessingJobs || [],
            discrepancyReports: data.discrepancyReports || [],
          };

          setProjectData(formattedData);
        } else {
          // If project not found in DB, fall back to demo data
          setProjectData(projectsData.demo);
          setError(t("project_not_found_db"));
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        setError(t("failed_to_load_project_data"));
        // Fall back to demo data on error
        setProjectData(projectsData.demo);
        bugsnagClient.notify(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectData();
  }, [id, t]);

  const handleSaveProject = async ({ name, description }) => {
    try {
      await api.updateProject(id, { name, description });

      setProjectData((prev) => ({
        ...prev,
        title: name,
        description,
      }));

      trackEvent(ANALYTICS_EVENTS.PROJECT_UPDATED, {
        projectId: id,
        projectTitle: name,
      });

      setIsEditing(false);
    } catch (err) {
      console.error("Error updating project:", err);
      bugsnagClient.notify(err);
      alert(err.message || t("project_edit.failed_to_update"));
      throw err;
    }
  };

  // Determine current tab from URL
  const getCurrentTab = () => {
    if (location.pathname.includes("/sections")) return "sections";
    if (location.pathname.includes("/billofquantities"))
      return "billofquantities";
    if (location.pathname.includes("/team")) return "team";
    return "sections"; // default
  };

  const activeTab = getCurrentTab();

  // Show loading state while fetching project data
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Show error message if there's an error
  if (error && !projectData) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">{t("error_bang")}</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div>
      {/* Show warning if we're using fallback data */}
      {error && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-gray-200">
        {isEditing ? (
          <ProjectEditInline
            title={projectData.title}
            description={projectData.description}
            onSave={handleSaveProject}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {projectData.title}
              </h1>
              <button
                onClick={() => setIsEditing(true)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                title={t("project_edit.edit_project")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
            </div>
            {projectData.description && (
              <p className="mt-1 text-sm text-gray-500">
                {projectData.description}
              </p>
            )}
          </div>
        )}
      </div>

      {/* PDS Integration Section */}
      <div className="px-6 pt-4">
        {pdsProjectId ? (
          <PdsProjectDetails
            pdsProjectId={pdsProjectId}
            projectId={id}
            onUnlinked={() => setPdsProjectId(null)}
          />
        ) : (
          <PdsProjectSelector
            projectId={id}
            onLinked={(uuid) => setPdsProjectId(uuid)}
          />
        )}
      </div>

      {/* Plan Upload Wizard */}
      {showPlanUpload && (
        <PlanUploadWizard
          onClose={() => setShowPlanUpload(false)}
          projectId={id}
          onUploadComplete={() => {}}
        />
      )}

      {/* Image Upload Wizard */}
      {showImageUpload && (
        <ImageUploadWizard
          onClose={() => setShowImageUpload(false)}
          projectId={id}
          onUploadComplete={() => {}}
        />
      )}

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === "sections" && (
          <SectionManagement
            projectId={id}
            userRole={userRole}
            isGlobalAdmin={isGlobalAdmin}
            sections={sections}
            setSections={setSections}
          />
        )}

        {activeTab === "billofquantities" && (
          <LV
            projectId={id}
            userRole={userRole}
            isGlobalAdmin={isGlobalAdmin}
            sections={sections}
            setSections={setSections}
          />
        )}

        {activeTab === "team" && (
          <TeamManagement
            projectId={id}
            userRole={userRole}
            isGlobalAdmin={isGlobalAdmin}
          />
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;
